import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [openC, setOpenC] = useState(false);
  const [openCt, setOpenCt] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", dext_email: "", notes: "" });
  const [contactForm, setContactForm] = useState({ phone: "", display_name: "", client_id: "" });

  const load = async () => {
    const [c, ct] = await Promise.all([api.get("/clients"), api.get("/contacts")]);
    setClients(c.data); setContacts(ct.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return;
    await api.post("/clients", form);
    setForm({ name: "", company: "", email: "", dext_email: "", notes: "" });
    setOpenC(false); toast.success("Client added"); load();
  };
  const updateClient = async (id, patch) => {
    await api.patch(`/clients/${id}`, patch);
    toast.success("Client updated"); load();
  };
  const createContact = async () => {
    if (!contactForm.phone) return;
    await api.post("/contacts", { ...contactForm, client_id: contactForm.client_id || null });
    setContactForm({ phone: "", display_name: "", client_id: "" });
    setOpenCt(false); toast.success("Contact linked"); load();
  };
  const del = async (id) => { await api.delete(`/clients/${id}`); toast.success("Client removed"); load(); };
  const delC = async (id) => { await api.delete(`/contacts/${id}`); toast.success("Contact removed"); load(); };
  const relink = async (contactId, clientId) => {
    const ct = contacts.find(c => c.id === contactId);
    await api.patch(`/contacts/${contactId}`, { phone: ct.phone, display_name: ct.display_name, client_id: clientId });
    toast.success("Mapping updated"); load();
  };

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));

  return (
    <div className="space-y-8" data-testid="clients-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Directory</div>
          <h1 className="mt-1 font-[Manrope] font-extrabold text-4xl tracking-tight">Clients &amp; contacts</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">Map each phone number to the business it belongs to. Flash reports go to every linked contact.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openC} onOpenChange={setOpenC}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-[#0F172A] hover:bg-[#0F172A]/90 text-white"><Plus size={16} className="mr-1.5"/> New client</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add client</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="client-name" placeholder="Business name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                <Input data-testid="client-company" placeholder="Legal / trading name" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} />
                <Input data-testid="client-email" placeholder="Primary email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                <Input data-testid="client-dext-email" placeholder="DEXT inbox email (unique per client)" value={form.dext_email} onChange={e => setForm({ ...form, dext_email: e.target.value })} />
                <Textarea data-testid="client-notes" placeholder="Notes…" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
              <DialogFooter><Button data-testid="client-save" onClick={create}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={openCt} onOpenChange={setOpenCt}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn" variant="outline"><Plus size={16} className="mr-1.5"/> New contact</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="contact-phone" placeholder="+15551234567" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} />
                <Input data-testid="contact-name" placeholder="Display name" value={contactForm.display_name} onChange={e => setContactForm({ ...contactForm, display_name: e.target.value })} />
                <Select value={contactForm.client_id} onValueChange={v => setContactForm({ ...contactForm, client_id: v })}>
                  <SelectTrigger data-testid="contact-client"><SelectValue placeholder="Link to client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter><Button data-testid="contact-save" onClick={createContact}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border border-slate-200 shadow-none rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 text-xs uppercase font-mono tracking-widest text-slate-500">Clients</div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/60">
              <TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>Email</TableHead>
              <TableHead>DEXT email</TableHead>
              <TableHead>Contacts</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">No clients yet.</TableCell></TableRow>}
            {clients.map(c => (
              <TableRow key={c.id} className="hover:bg-slate-50 transition-colors" data-testid={`client-row-${c.id}`}>
                <TableCell className="font-semibold">{c.name}</TableCell>
                <TableCell>{c.company || "—"}</TableCell>
                <TableCell className="font-mono text-sm">{c.email || "—"}</TableCell>
                <TableCell>
                  <Input
                    data-testid={`client-dext-${c.id}`}
                    defaultValue={c.dext_email || ""}
                    placeholder="dext@…"
                    className="h-8 font-mono text-xs w-[220px]"
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v === (c.dext_email || "")) return;
                      updateClient(c.id, { dext_email: v || null });
                    }}
                  />
                </TableCell>
                <TableCell>{contacts.filter(x => x.client_id === c.id).length}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`del-client-${c.id}`} variant="ghost" size="sm" onClick={() => del(c.id)}><Trash size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="border border-slate-200 shadow-none rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 text-xs uppercase font-mono tracking-widest text-slate-500">Contact ↔ Client mapping</div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/60">
              <TableHead>Phone</TableHead><TableHead>Display name</TableHead><TableHead>Linked client</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-slate-500">No contacts yet.</TableCell></TableRow>}
            {contacts.map(c => (
              <TableRow key={c.id} className="hover:bg-slate-50 transition-colors" data-testid={`contact-row-${c.id}`}>
                <TableCell className="font-mono">{c.phone}</TableCell>
                <TableCell>{c.display_name || "—"}</TableCell>
                <TableCell>
                  <Select value={c.client_id || ""} onValueChange={(v) => relink(c.id, v)}>
                    <SelectTrigger data-testid={`relink-${c.id}`} className="w-[240px]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(cl => <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`del-contact-${c.id}`} variant="ghost" size="sm" onClick={() => delC(c.id)}><Trash size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
